
import { RevContainer } from '@ds/components/Container'
import isEmpty from 'lodash/isEmpty'

import { dynamicImport, spacesToHyphens } from '@core/helpers'
import { generateAlternateLinks, getMetas } from '@core/helpers/head'
import { getModelsComparisonResults } from '@http/endpoints'
import logger from '@logger'
import { ROUTES } from '@router'

import AdditionalContent from '../../components/AdditionalContent/AdditionalContent.vue'
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb.vue'
import ComparisonForm from '../../components/ComparisonForm/ComparisonForm.vue'

import translations from './ModelsComparisonResults.translations'

const ComparisonResults = () =>
  dynamicImport(
    import('../../components/ComparisonResults/ComparisonResults.vue'),
  )

function hasModelsInRoute(route) {
  return !isEmpty(route.params.productA) && !isEmpty(route.params.productB)
}

async function getAlternateLinks(hreflangs, store) {
  if (isEmpty(hreflangs)) {
    return []
  }

  try {
    const alternateLinks = await generateAlternateLinks({
      // `hreflangs` have all the same `link` value
      link: hreflangs[0].link,
      baseUrl: store.getters['config/baseURL'],
      countries: hreflangs.map((link) => link.country),
    })

    return alternateLinks
  } catch (error) {
    logger.error(error?.message, { error })

    return []
  }
}

function fetchModelsComparisonResults(store, route) {
  const { productA, productB } = route.params

  return store.dispatch('http/request', {
    request: getModelsComparisonResults,
    pathParams: {
      model1: productA,
      model2: productB,
    },
  })
}

function isCurrentUrlCanonical(currentRoute, toRoute) {
  return currentRoute.href === toRoute.href
}

export default {
  components: {
    AdditionalContent,
    Breadcrumb,
    ComparisonForm,
    ComparisonResults,
    RevContainer,
  },

  async asyncData({ route, store, app, redirect }) {
    try {
      const { payload } = await fetchModelsComparisonResults(store, route)

      const { algoliaConfig, category, content, models } = payload
      const {
        customContent,
        featuredArticles,
        suggestedComparisons,
        hreflangs,
      } = content
      const { metaImage, metaTitle, metaDescription } = customContent

      // Redirect to existing URL if there's one
      const { link } =
        hreflangs?.find(
          (item) => item.hreflang === store.getters['config/locale'],
        ) || {}

      if (link) {
        const targetLink = { ...link }
        // Fix the hash that's returned as empty object, and messes the URL
        targetLink.hash = ''

        const currentRoute = app.$navigation.resolve(route)
        const toRoute = app.$navigation.resolve(targetLink)

        const isModelsOrderInUrlCorrect = isCurrentUrlCanonical(
          currentRoute,
          toRoute,
        )

        if (!isModelsOrderInUrlCorrect) {
          redirect(301, toRoute.route)

          return {}
        }
      }

      const alternateLinks = await getAlternateLinks(hreflangs, store)

      return {
        additionalContent: {
          customContent,
          featuredArticles,
          suggestedComparisons,
        },
        autocompleteConfig: algoliaConfig.searchBox,
        category,
        comparisonConfig: algoliaConfig.comparison,
        headTags: { alternateLinks, metaImage, metaTitle, metaDescription },
        selectedModels: models,
        selectedModelsNames: models.map(({ compareName }) => compareName),
      }
    } catch (error) {
      logger.error('Error fetching models comparison results', {
        error,
        models: {
          productB: route.params.productB,
          productA: route.params.productA,
        },
      })

      const statusCode = error.status_code === 404 ? 404 : 500
      redirect(statusCode)

      return {}
    }
  },

  data() {
    return {
      additionalContent: {},
      autocompleteConfig: {},
      category: {},
      comparisonConfig: [],
      headTags: {},
      selectedModels: [],
      selectedModelsNames: [],
    }
  },

  head() {
    return {
      title: this.headTags.metaTitle,
      meta: [
        ...getMetas({
          title: this.headTags.metaTitle,
          description: this.headTags.metaDescription,
          image: this.headTags.metaImage,
        }),
      ],
      link: this.headTags.alternateLinks,
    }
  },

  computed: {
    hasModelsToCompare() {
      return hasModelsInRoute(this.$route) && this.selectedModels.length >= 2
    },

    title() {
      return this.$t(translations.title, {
        firstModel: this.selectedModelsNames[0],
        secondModel: this.selectedModelsNames[1],
      })
    },

    selectedModelsAsSlugs() {
      return {
        productA: spacesToHyphens(this.selectedModelsNames[0]).toLowerCase(),
        productB: spacesToHyphens(this.selectedModelsNames[1]).toLowerCase(),
      }
    },
  },

  watch: {
    selectedModelsNames: {
      handler(models) {
        if (models.length >= 2) {
          this.updateUrl()
        }
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    handleModelSelection({ model, inputId }) {
      this.selectedModelsNames.splice(inputId, 1, model.name)
    },

    updateUrl() {
      const { productA: modelSelected1, productB: modelSelected2 } =
        this.selectedModelsAsSlugs
      const { productA: modelInUrl1, productB: modelInUrl2 } =
        this.$route.params
      const areSelectedModelsInUrl =
        modelSelected1 === modelInUrl1 && modelSelected2 === modelInUrl2

      if (!areSelectedModelsInUrl) {
        this.$navigation.push({
          type: 'internal',
          name: ROUTES.MODELS_COMPARISON.COMPARISON_RESULTS,
          params: { productA: modelSelected1, productB: modelSelected2 },
        })
      }
    },
  },
}
